import { apiRequest } from "../client";
import type { Document, DocumentChatResponse } from "./types";
import type { PaginatedResponse, SearchParams } from "../types";

/**
 * List all files
 */
export const getDocuments = async (
  params?: SearchParams
): Promise<PaginatedResponse<Document>> => {
  const response = await apiRequest<PaginatedResponse<Document>>({
    method: "GET",
    url: `/documents/search`,
    params,
  });
  return response;
};

/**
 * Get all documents for a product
 */
export const getProductDocuments = async (
  productId: string
): Promise<Document[]> => {
  const response = await apiRequest<{ documents: Document[] }>({
    method: "GET",
    url: `/documents/search?resource_type=product&resource_id=${productId}`,
  });
  return response.documents || [];
};

/**
 * Get all documents for a portfolio
 */
export const getPortfolioDocuments = async (
  portfolioId: string
): Promise<Document[]> => {
  const response = await apiRequest<{ documents: Document[] }>({
    method: "GET",
    url: `/documents/search?resource_type=portfolio&resource_id=${portfolioId}`,
  });
  return response.documents || [];
};

/**
 * Upload a new document
 */
export const uploadDocument = async (
  file: File,
  options?: {
    resource_type?: string;
    resource_id?: string;
    process_in_background?: boolean;
  }
): Promise<Document> => {
  const formData = new FormData();

  // Required field
  formData.append("file", file);

  // Optional fields with their defaults
  formData.append(
    "process_in_background",
    (options?.process_in_background ?? false).toString()
  );

  // Optional resource linking
  if (options?.resource_type) {
    formData.append("resource_type", options.resource_type);
  }
  if (options?.resource_id) {
    formData.append("resource_id", options.resource_id);
  }

  console.log("Uploading document with options:", {
    resource_type: options?.resource_type,
    resource_id: options?.resource_id,
    process_in_background: options?.process_in_background ?? false,
  });

  const response = await apiRequest<{
    document_id: string;
    filename: string;
    file_type: string;
    chunk_count: number;
    resource_type: string | null;
    resource_id: string | null;
    metadata: {
      title?: string;
      author?: string;
      creation_date?: string;
      pages?: number;
      user?: {
        email: string;
        company_id: string;
        upload_timestamp: string;
        resource_type: string;
        resource_id: string;
      };
      processing_timestamp?: string;
    };
  }>({
    method: "POST",
    url: `/documents/upload`,
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  // Transform the response into our Document type
  const document: Document = {
    document_id: response.document_id,
    filename: response.filename,
    file_type: response.file_type,
    upload_date:
      response.metadata?.user?.upload_timestamp || new Date().toISOString(),
    user_id: response.metadata?.user?.company_id || "",
    resource_type:
      options?.resource_type ||
      response.metadata?.user?.resource_type ||
      response.resource_type,
    resource_id:
      options?.resource_id ||
      response.metadata?.user?.resource_id ||
      response.resource_id,
    metadata: response.metadata || {},
    chunk_count: response.chunk_count,
    display_name: response.metadata?.title || response.filename,
    original_filename: response.filename,
    landscape: false
  };

  return document;
}

/**
 * Send a chat query about documents
 */
export const sendDocumentChatQuery = async (
  message: string,
  conversationHistory: { role: "user" | "assistant"; content: string }[] = []
): Promise<DocumentChatResponse> => {
  return apiRequest<DocumentChatResponse>({
    method: "POST",
    url: `/documents/query`,
    data: {
      question: message,
      conversation_history: conversationHistory,
    },
  });
};

/**
 * Get a document by ID
 */
export const getDocument = async (documentId: string): Promise<Document> => {
  const response = await apiRequest<Document>({
    method: "GET",
    url: `/documents/${documentId}`,
  });
  return response;
};

/**
 * Update an existing document
 */
export const updateDocument = async (
  documentId: string,
  data: {
    name: string;
    body: any;
    as_at: string;
  }
): Promise<Document> => {
  const response = await apiRequest<Document>({
    method: "PUT",
    url: `/documents/${documentId}`,
    data,
  });
  return response;
};

/**
 * Delete a document by ID
 */
export const deleteDocument = async (documentId: string): Promise<void> => {
  await apiRequest({
    method: "DELETE",
    url: `/documents/${documentId}`,
  });
};

/**
 * Get all available document tags
 */
export const getDocumentTags = async (params?: {
  resource_type?: string;
  resource_id?: string;
}): Promise<string[]> => {
  const response = await apiRequest<string[]>({
    method: "GET",
    url: `/documents/tags`,
    params,
  });
  return response;
};

/**
 * Download one or more documents as PDF or ZIP.
 *
 * Single document: Returns PDF directly
 * Multiple documents: Returns ZIP archive
 *
 * @param documentIds - Array of document IDs to download
 * @param filename - Custom filename for ZIP (optional, default: "documents")
 * @param includeManifest - Include manifest JSON in ZIP (optional, default: true)
 * @returns Promise that resolves when download completes
 */
export const downloadDocuments = async (
  documentIds: string[],
  filename?: string,
  includeManifest: boolean = true
): Promise<void> => {
  const { apiRequestWithHeaders } = await import("../client");

  try {
    const response = await apiRequestWithHeaders<Blob>({
      method: "POST",
      url: `/documents/download`,
      data: {
        document_ids: documentIds,
        filename: filename || "documents",
        include_manifest: includeManifest,
      },
      responseType: "blob",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/zip, application/pdf",
      },
    });

    // Determine file type from Content-Type header
    const contentType = response.headers["content-type"] || "";
    const isPdf = contentType.includes("application/pdf");
    const isZip = contentType.includes("application/zip");

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers["content-disposition"] || "";
    let downloadFilename = filename || "documents";

    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
    if (filenameMatch && filenameMatch[1]) {
      downloadFilename = filenameMatch[1];
    } else {
      // Add appropriate extension if not present
      if (isPdf && !downloadFilename.endsWith(".pdf")) {
        downloadFilename += ".pdf";
      } else if (isZip && !downloadFilename.endsWith(".zip")) {
        downloadFilename += ".zip";
      }
    }

    // Create blob URL and trigger download
    const blob = response.data;
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = downloadFilename;
    document.body.appendChild(anchor);
    anchor.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(anchor);

    // Log success metrics
    const documentCount = response.headers["x-document-count"];
    const failedCount = response.headers["x-failed-count"];

    console.log("Download completed:", {
      documentCount,
      failedCount,
      fileType: isPdf ? "pdf" : "zip",
      filename: downloadFilename,
    });

    if (failedCount && parseInt(failedCount) > 0) {
      console.warn(`${failedCount} documents failed to download`);
    }
  } catch (error) {
    console.error("Error downloading documents:", error);
    throw error;
  }
};
